import React, {
  useContext,
  useRef,
  useMemo,
  useCallback,
  useEffect,
  useState,
  createContext,
  PropsWithChildren,
  EffectCallback,
  DependencyList,
} from "react";
import useCountdown from "@root/misc/useCountdown";
import {
  ILineup,
  LineupEditAction,
  LineupID,
  LineupOption,
  LineupOptionID,
  LineupOptionsVersion,
  LineupSlotInfoWithPickedOption,
  posForSlot,
  SlotKey,
} from "../types";
import { LineupApiContext, LineupApiProvider } from "./LineupApiContext";
import _ from "lodash";
import { ErrorResponse } from "../actions";

const LineupCountdownContext = createContext({});

const LineupCountdownProvider = ({ children }: PropsWithChildren<{}>) => {
  const { lineup } = useContext(LineupContext);

  const countdown = useCountdown({ secondsLeft: lineup?.countdown_sec ?? 0 });

  return (
    <LineupCountdownContext.Provider value={countdown}>
      {children}
    </LineupCountdownContext.Provider>
  );
};

export const useLineupCountdown = () => {
  const { lineup } = useContext(LineupContext);

  return useCountdown({ secondsLeft: lineup?.countdown_sec ?? 0 });
};

type Countdown = {
  d: string;
  hh: string;
  mm: string;
  ss: string;
  totalSecondsLeft: number;
};

const useCountdownEffect = (
  effect: (countdown: Countdown) => ReturnType<EffectCallback>,
  deps?: DependencyList
) => {
  const countdown = useLineupCountdown();

  useEffect(() => {
    effect(countdown);
  }, [...(deps ?? []), countdown.totalSecondsLeft]);
};

export type SyncStatus = "synching" | "synchingDone" | "couldNotSync";

export type CompleteLineupContext = ILineupContext &
  Required<Pick<ILineupContext, "lineup" | "options" | "optionsVersion">>;

export interface ILineupContext {
  lineup: ILineup | undefined;
  options: LineupOption[] | undefined;
  getOption: (optionId?: LineupOptionID) => LineupOption | undefined;
  optionsVersion: LineupOptionsVersion | undefined;
  getSlotIfPicked: (
    slotKey: SlotKey
  ) => LineupSlotInfoWithPickedOption | undefined;
  action(actions: LineupEditAction[]): void;
  numPickedOptionsWithoutGermanLicense: number;
  lineupSyncStatus?: SyncStatus;
  optionsSyncStatus?: SyncStatus;
  requestError: { reason: string } | undefined;
  resetRequestError: () => void;
  decideWhichActionsToSendForAPick(
    slot: SlotKey,
    optionId: LineupOptionID | undefined
  ): LineupEditAction[] | undefined;
  isAlreadyInPicks(optionId: LineupOptionID): SlotKey | undefined;
  getSlotForOption(
    optionId: LineupOptionID
  ): LineupSlotInfoWithPickedOption | undefined;
}

export interface BootLineupProps {
  authToken: string;
  id: LineupID;
  initialLineup?: ILineup;
  initialOptions?: LineupOption[];
  initialLoadingPlaceholder?: React.ReactNode;
}

export const LineupContext = React.createContext<ILineupContext>({} as any); // nothing here!

const calcNumPickedOptionsWithoutGermanLicense = (
  slots: ILineup["slots"] | undefined,
  getOption: ILineupContext["getOption"]
): number => {
  if (!slots) {
    return 0;
  }
  return _.filter(_.values(slots), (val) => {
    const option = getOption(val?.pick);
    if (!option) {
      return false;
    }
    return !option.german_license;
  }).length;
};

interface PartialLineupContextVal {
  action: (actions: LineupEditAction[]) => void;
  lineup?: ILineup;
  options?: LineupOption[];
  optionsVersion?: string;
  lineupSyncStatus?: SyncStatus;
  optionsSyncStatus?: SyncStatus;
  requestError: { reason: string } | undefined;
  resetRequestError: () => void;
}

export const useLineupContextVal = ({
  action,
  lineup,
  options,
  optionsVersion,
  lineupSyncStatus,
  optionsSyncStatus,
  requestError,
  resetRequestError,
}: PartialLineupContextVal) => {
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const getOption = useCallback(
    (optionId?: LineupOptionID) =>
      optionId
        ? _.find(optionsRef.current, (o: LineupOption) => o.id === optionId)
        : undefined,
    []
  );

  const slots = lineup?.slots;
  const slotsRef = useRef(slots);
  slotsRef.current = slots;
  const numPickedOptionsWithoutGermanLicense = useMemo(() => {
    return calcNumPickedOptionsWithoutGermanLicense(slots, getOption);
  }, [slots]);

  const getSlotIfPicked = useCallback(
    (slotKey: SlotKey): LineupSlotInfoWithPickedOption | undefined => {
      if (!slotsRef.current) {
        return undefined;
      }
      const slot = slotsRef.current[slotKey];
      if (!slot || !slot.pick) {
        return undefined;
      }
      const pickedOption = getOption(slot.pick);

      return {
        ...slot,
        slotKey,
        pick: slot.pick!,
        pickedOption: pickedOption || {
          id: "does-not-exist",
          german_license: true,
          image_url: "",
          name: "",
          position: posForSlot(slotKey),
          price: 0,
          team: "",
          active: true,
          short_name: "",
          country_abbr: "",
        },
      };
    },
    [getOption]
  );

  const isAlreadyInPicks = useCallback(
    (optionId: LineupOptionID): SlotKey | undefined => {
      if (!slotsRef.current) {
        return undefined;
      }
      return _.find(
        _.keys(slotsRef.current) as SlotKey[],
        (key) => slotsRef.current![key]?.pick === optionId
      );
    },
    []
  );

  const getSlotForOption = useCallback(
    (optionId: LineupOptionID) => {
      const slotKey = isAlreadyInPicks(optionId);
      return slotKey && getSlotIfPicked(slotKey);
    },
    [isAlreadyInPicks, getSlotIfPicked]
  );

  const decideWhichActionsToSendForAPick = useCallback(
    (
      slot: SlotKey,
      pick: LineupOptionID | undefined
    ): LineupEditAction[] | undefined => {
      if (!pick) {
        return [{ type: "clear_slot", slot }];
      }

      const pickIsAlreadyInLineupOnThisSlot = getSlotForOption(pick);
      if (pickIsAlreadyInLineupOnThisSlot) {
        if (pickIsAlreadyInLineupOnThisSlot.slotKey === slot) {
          return undefined;
        } // just picked same option again

        return [
          {
            type: "swap",
            slot1: slot,
            slot2: pickIsAlreadyInLineupOnThisSlot.slotKey,
          },
        ];
      } else {
        return [{ type: "pick", slot, option: pick }];
      }
    },
    [getSlotForOption]
  );

  const contextVal = useMemo(() => {
    return {
      lineup,
      options,
      getOption,
      optionsVersion,
      action,
      lineupSyncStatus,
      optionsSyncStatus,
      requestError,
      resetRequestError,
      numPickedOptionsWithoutGermanLicense,
      getSlotIfPicked,
      decideWhichActionsToSendForAPick,
      isAlreadyInPicks,
      getSlotForOption,
    };
  }, [
    lineup,
    options,
    optionsVersion,
    action,
    lineupSyncStatus,
    optionsSyncStatus,
    requestError,
    resetRequestError,
    getOption,
    numPickedOptionsWithoutGermanLicense,
    getSlotIfPicked,
  ]);

  return contextVal;
};

export const isCompleteLineupContext = (
  val: ILineupContext
): val is CompleteLineupContext => {
  return !!val.action && !!val.lineup && !!val.options && !!val.optionsVersion;
};

const needsNewOptions = (
  optionsVersion: LineupOptionsVersion | undefined,
  optionsVersionFromResponse: LineupOptionsVersion
) => {
  return !optionsVersion || optionsVersion !== optionsVersionFromResponse;
};

const isError = (resp: unknown): resp is ErrorResponse => {
  return !!(resp as ErrorResponse).error;
};

interface InnerContextState {
  lineup?: ILineup;
  options?: LineupOption[];
  optionsVersion?: LineupOptionsVersion;
}

const BootLineupContextInner: React.FC<
  React.PropsWithChildren<BootLineupProps>
> = React.memo(({ children, id: lineupId, ...props }) => {
  const lineupApi = useContext(LineupApiContext);
  const lineupApiRef = useRef(lineupApi);
  lineupApiRef.current = lineupApi;

  const [lineupSyncStatus, setLineupSyncStatus] = useState<
    SyncStatus | undefined
  >();
  const [optionsSyncStatus, setOptionsSyncStatus] = useState<
    SyncStatus | undefined
  >();
  const [requestError, setRequestError] = useState<
    { reason: string } | undefined
  >(undefined);

  const [{ lineup, options, optionsVersion }, setState] =
    useState<InnerContextState>({
      // initial state
      lineup: props.initialLineup,
      options: props.initialOptions,
    });
  const optionsVersionRef = useRef(optionsVersion);
  optionsVersionRef.current = optionsVersion;

  const requestNewOptions = useCallback(() => {
    setOptionsSyncStatus("synching");
    lineupApiRef.current
      .getOptions()
      .then((resp) => {
        setOptionsSyncStatus("synchingDone");
        setState((prev) => {
          return {
            ...prev,
            options: resp.options,
            optionsVersion: resp.options_version,
          };
        });
      })
      .catch(() => {
        setOptionsSyncStatus("couldNotSync");
      });
  }, []);

  const requestNewOptionsIfNecessary = useCallback(
    (optionsVersionFromResponse: LineupOptionsVersion) => {
      if (
        needsNewOptions(optionsVersionRef.current, optionsVersionFromResponse)
      ) {
        requestNewOptions();
      }
    },
    [requestNewOptions]
  );

  const requestLineup = useCallback(() => {
    lineupApi
      .get(false)
      .then((resp) => {
        if (isError(resp)) {
          setRequestError({ reason: resp.error });
          return;
        }

        setState((prev) => ({
          ...prev,
          lineup: resp.lineup,
          options: resp.picked_options ?? prev.options,
        }));
      })
      .catch(() => {
        setLineupSyncStatus("couldNotSync");
        // could not poll
      });
  }, [requestNewOptionsIfNecessary]);

  useEffect(() => {
    // request lineup if not given initially
    if (!lineup) {
      requestLineup();
    }
  }, [!!lineup, requestLineup]);

  useEffect(() => {
    // request options if not given initially
    if (!options) {
      requestNewOptions();
    }
  }, [!!options, requestNewOptions]);

  useCountdownEffect(
    (countdown) => {
      // countdown over but lineup still open? poll!
      let timeout: number;
      if (lineup?.phase === "open" && countdown?.totalSecondsLeft === 0) {
        // countdown ran out: poll in a sec and again after a few sec
        timeout = window.setTimeout(() => {
          requestLineup();
          timeout = window.setTimeout(requestLineup, 5000);
        }, 2000);
      }

      return () => {
        // cleanup
        if (typeof timeout === "number") {
          window.clearTimeout(timeout);
        }
      };
    },
    [lineup?.phase]
  );

  useEffect(() => {
    // poll for scores-update
    let interval: number;
    if (lineup?.phase === "closed" || lineup?.phase === "scored") {
      interval = window.setInterval(() => {
        lineupApi
          .get(true)
          .then((resp) => {
            if (isError(resp)) {
              setRequestError({ reason: resp.error });
              return;
            }

            setState((prev) => ({
              ...prev,
              lineup: resp.lineup,
              options: resp.picked_options ?? prev.options,
            }));
          })
          .catch(() => {
            // could not poll, but don't show error, it's just for scoring update
          });
      }, 2 * 60 * 1000); // every 2 min
    }

    return () => {
      // cleanup
      if (typeof interval === "number") {
        window.clearInterval(interval);
      }
    };
  }, [lineup?.phase]);

  const action = useCallback(
    (actions: LineupEditAction[]) => {
      setLineupSyncStatus("synching");
      lineupApiRef.current
        .action(actions)
        .then((resp) => {
          setLineupSyncStatus("synchingDone");

          if (isError(resp)) {
            setRequestError({ reason: resp.error });

            requestLineup();
          } else {
            setState((prev) => ({
              ...prev,
              lineup: resp.lineup,
            }));

            requestNewOptionsIfNecessary(resp.options_version);
          }
        })
        .catch(() => {
          setLineupSyncStatus("couldNotSync");
        });
    },
    [lineupId, optionsVersion, requestNewOptions]
  );

  const resetRequestError = useCallback(() => {
    setRequestError(undefined);
  }, []);

  const contextVal = useLineupContextVal({
    action,
    lineup,
    options,
    optionsVersion,
    lineupSyncStatus,
    optionsSyncStatus,
    requestError,
    resetRequestError,
  });

  return (
    <LineupContext.Provider value={contextVal}>
      <LineupCountdownProvider>{children}</LineupCountdownProvider>
    </LineupContext.Provider>
  );
});

export const BootLineupContext: React.FC<
  React.PropsWithChildren<BootLineupProps>
> = (props) => {
  return (
    <LineupApiProvider authToken={props.authToken} lineupId={props.id}>
      <BootLineupContextInner {...props} />
    </LineupApiProvider>
  );
};
